import { useState } from "react";
import { odooProductionMigrationService } from "../services/odooProductionMigrationService";
import { distributionService } from "../services/distributionService";
import { apiClient } from "../services/apiClient";
import type { ProductionMigrationStatusDto } from "../types/odoo-migration";

interface OpenAiClassificationResult {
  total: number;
  classified: number;
  unmatched: number;
  skipped: boolean;
  reason?: string;
}

export interface SnackbarState {
  open: boolean;
  message: string;
  severity: "success" | "error" | "warning" | "info";
}

/**
 * Hook que encapsula los handlers + estado de la página
 * `OdooProductionSyncPage`. Extraído como helper para mantener la página
 * ≤500L (P7). Sin cambio funcional vs versión inline.
 */
export function useOdooProductionSyncHandlers() {
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: "",
    severity: "info",
  });
  const [migrationStatus, setMigrationStatus] =
    useState<ProductionMigrationStatusDto | null>(null);
  const [monitorKey, setMonitorKey] = useState(0);
  const [isRunningPostMigration, setIsRunningPostMigration] = useState(false);
  const [isRunningDistribution, setIsRunningDistribution] = useState(false);
  const [isRunningClassification, setIsRunningClassification] = useState(false);

  const handleMigrationStarted = () => {
    setMonitorKey((prev) => prev + 1);
    setSnackbar({
      open: true,
      message:
        "Sincronizacion manual iniciada exitosamente. El monitor se actualiza automaticamente.",
      severity: "success",
    });
  };

  const handleMigrationError = (error: string) => {
    setSnackbar({
      open: true,
      message: `Error en sincronizacion: ${error}`,
      severity: "error",
    });
  };

  const handleStatusChange = (status: ProductionMigrationStatusDto) => {
    setMigrationStatus(status);
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleRunDistributionOnly = async () => {
    setIsRunningDistribution(true);
    try {
      const response = await distributionService.triggerPostSyncOrchestrator();
      if (response.ok) {
        setSnackbar({
          open: true,
          message:
            "Recálculo de distribución ejecutado. Revisa /compras/distribucion/transferencias para ver tiendas con líneas pendientes.",
          severity: "success",
        });
      } else {
        setSnackbar({
          open: true,
          message: "El orquestador respondió sin ok=true.",
          severity: "warning",
        });
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Error desconocido";
      setSnackbar({
        open: true,
        message: `Error en recálculo de distribución: ${errorMessage}`,
        severity: "error",
      });
    } finally {
      setIsRunningDistribution(false);
    }
  };

  const handleRunPostMigrationOnly = async () => {
    setIsRunningPostMigration(true);
    try {
      const response =
        await odooProductionMigrationService.triggerPostMigrationOnly({
          skipClassification: true,
        });
      if (response.success) {
        setMonitorKey((prev) => prev + 1);
        setSnackbar({
          open: true,
          message:
            "Post-migracion iniciada (sin clasificacion OpenAI). El progreso se muestra en el panel de estado.",
          severity: "info",
        });
      } else {
        setSnackbar({
          open: true,
          message: response.message,
          severity: "error",
        });
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Error desconocido";
      setSnackbar({
        open: true,
        message: `Error: ${errorMessage}`,
        severity: "error",
      });
    } finally {
      setIsRunningPostMigration(false);
    }
  };

  const handleRunOpenAiClassification = async () => {
    setIsRunningClassification(true);
    try {
      const res = await apiClient.post<OpenAiClassificationResult>(
        "/forecasting/openai-classify",
      );
      const { total, classified, unmatched, skipped, reason } = res.data;
      if (skipped) {
        setSnackbar({
          open: true,
          message: `Clasificacion OpenAI omitida: ${reason ?? "razon desconocida"}`,
          severity: "warning",
        });
      } else {
        setSnackbar({
          open: true,
          message: `Clasificacion OpenAI completada: ${classified} clasificados, ${unmatched} sin clasificar de ${total} pendientes.`,
          severity: "success",
        });
      }
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Error desconocido";
      setSnackbar({
        open: true,
        message: `Error en clasificacion OpenAI: ${errorMessage}`,
        severity: "error",
      });
    } finally {
      setIsRunningClassification(false);
    }
  };

  return {
    snackbar,
    migrationStatus,
    monitorKey,
    isRunningPostMigration,
    isRunningDistribution,
    isRunningClassification,
    handleMigrationStarted,
    handleMigrationError,
    handleStatusChange,
    handleCloseSnackbar,
    handleRunDistributionOnly,
    handleRunPostMigrationOnly,
    handleRunOpenAiClassification,
  };
}
